// ScanReview — the post-UPC-scan review screen. Replaces the old
// "auto-fill the form, hope the user notices" pattern with an
// explicit "here's what we found / here's what's missing" surface.
//
// Why this exists:
//   The old AddDraftSheet auto-populated 30+ fields the moment a
//   scan resolved (lookupBarcode + correction merge). The user had
//   no clear sense of what came from the scan vs what they typed,
//   so when something was wrong (frozen italian sausage tagged as
//   pantry meat) they didn't know WHICH layer to override.
//
// New pattern:
//   - Two visual buckets: FOUND (filled) and MISSING (empty).
//   - FOUND axes are still tappable (to override).
//   - MISSING axes show the same chip in unset state.
//   - The bottom actions are explicit: Place / Photo / Manual.
//
// Source attribution:
//   The store keeps a `sources` map (axis → "user"|"correction"|"off"
//   |"usda"|"infer"|"default"). ScanReview reads that map to decide
//   which bucket each axis lands in. "user", "correction", "off",
//   "usda", "infer" → FOUND. "default", null/missing → MISSING.
//
// Usage:
//   <ScanReview
//     draft={store.getState()}
//     onOpenAxis={(axis) => openPicker(axis)}
//     onPlace={() => store.commit(...)}
//     onPhoto={() => switchToPhotoPipeline()}
//     onManual={() => switchToManualPipeline()}
//     placing={committing}
//   />

import { useState } from "react";
import { font, color, radius, space } from "../../../lib/mcmTokens";
import { CHIP_TONES, SET_CHIP, UNSET_CHIP } from "../../../lib/tokens";
import { TierBadge } from "../../../lib/TierBadge";
import { findIngredient, findHub } from "../../../data/ingredients";

// Which axes count as "filled" vs "missing", based on the store's
// `sources` map and the actual value. An axis is FOUND if:
//   - It has a non-null value, AND
//   - Its source is one of: user, correction, off, usda, infer
// "default" is treated as MISSING because that means we hit the
// hard-default fallback (e.g. location → "pantry" because nothing
// upstream filled it). Surfacing those as "found" would mislead.
// Keep in sync with scanShared.jsx's FOUND_SOURCES. "ai-verified"
// (Haiku-accepted canonical), "kitchen-home" (household placement),
// "ai-recipe-add" (AIRecipe + IN PANTRY seeds), and "off-segment"
// (variant segmentation) were missing here — set axes from those
// sources rendered in the MISSING bucket while Place was enabled.
const FOUND_SOURCES = new Set(["user", "correction", "off", "usda", "infer", "autofill", "canonical-info", "tag-hints", "name-infer", "upc-correction", "canonical-brand-correction", "ai-verified", "kitchen-home", "ai-recipe-add", "off-segment"]);

// Axis definitions — ordered as a breadcrumb the user reads
// left-to-right: where it lives → what shelf → what hub family →
// the specific canonical → brand/state/cut/size details.
//
// "category" is intentionally DROPPED — it duplicated the tile in
// every UI we showed (DAIRY/DAIRY rows). The tile already carries
// the categorical signal that matters; the redundant chip just
// added noise.
//
// "hub" is a synthetic axis derived from the canonical's parentId
// at render time (gjetost.parentId="cheese_hub" → "Cheese"). Lets
// the breadcrumb read PANTRY → DAIRY → CHEESE → GJETOST instead
// of skipping the family layer.
const AXIS_DEFS = [
  { key: "location",    label: "Location",  tone: CHIP_TONES.location },
  { key: "tileId",      label: "Tile",      tone: CHIP_TONES.location },
  { key: "hub",         label: "Hub",       tone: CHIP_TONES.canonical, synthetic: true },
  { key: "canonicalId", label: "Canonical", tone: CHIP_TONES.canonical, displayKey: "canonicalName", required: true },
  { key: "brand",       label: "Brand",     tone: CHIP_TONES.canonical },
  { key: "state",       label: "State",     tone: CHIP_TONES.state },
  { key: "cut",         label: "Cut",       tone: CHIP_TONES.cut },
];

// Synthetic-axis support. The "hub" chip isn't stored on draft —
// it's derived from the bound canonical's parentId at render
// time. Lookup goes through findIngredient + findHub so the user
// sees a clean "Cheese" / "Beef" / "Pasta" hub label instead of
// the parentId slug.
function hubLabelForCanonical(canonicalId) {
  if (!canonicalId) return null;
  const ing = findIngredient(canonicalId);
  if (!ing?.parentId) return null;
  const hub = findHub(ing.parentId);
  return hub?.name || null;
}

function isFound(draft, axis) {
  if (axis.synthetic && axis.key === "hub") {
    return !!hubLabelForCanonical(draft.canonicalId);
  }
  const val = axis.key === "canonicalId"
    ? draft.canonicalId
    : draft[axis.key];
  if (val == null || val === "") return false;
  const src = draft.sources?.[axis.key];
  if (!src) return false;
  return FOUND_SOURCES.has(src);
}

function axisDisplayValue(draft, axis) {
  if (axis.synthetic && axis.key === "hub") {
    return hubLabelForCanonical(draft.canonicalId);
  }
  if (axis.key === "canonicalId") {
    return draft.canonicalName || draft.canonicalId;
  }
  if (axis.key === "tileId" && draft.tileId) {
    return draft.tileId.replace(/_/g, " ");
  }
  return draft[axis.key];
}

export default function ScanReview({
  draft,
  onOpenAxis,
  onPlace,
  onPhoto,
  onManual,
  onEditSize,
  // Add-to-shopping-list destination CTA. Optional — surfaces only
  // when the host passes a handler (the wizard's done step and the
  // scan/photo pipelines do; ResolveCard's wizard handoff doesn't).
  // This is what makes ScanReview THE shared confirm surface: one
  // review, two destinations, regardless of entry pipeline.
  onAddToList,
  // The wizard hosts this inside its own Step chrome (kicker+title),
  // where the built-in "Scan review / UPC…" header would double up
  // and read wrong for a browse flow.
  showHeader = true,
  // Axes the host has no editor for. The wizard passes
  // ["state","cut"] — state is inferred-never-surfaced and cuts are
  // baked into canonicals, so a "+ cut" chip whose tap does nothing
  // would read as broken.
  hiddenAxes = [],
  // Open the bind-via-photo flow (MemoryBookCapture → PhotoPipeline).
  // Required when OFF staged a proposedCanonical with no bundled
  // match — instead of rendering an inline mint card here, we route
  // out to the photo flow that owns the Haiku resolver + mint
  // aesthetic. Same pattern Cook Mode uses to reuse the scanner UI
  // rather than duplicating it per surface.
  onOpenPhotoCapture,
  // Dismiss the proposal without minting. Pipeline clears the
  // proposal from the draft. The user can still bind a canonical
  // manually via the canonical chip → search picker.
  onDismissProposal,
  placing = false,
}) {
  // Per-axis applicability — some axes only make sense in certain
  // hub contexts. Cut is meat-anatomical (breast/thigh/chuck/loin)
  // and must NOT show for non-meat canonicals. Cheese, dairy,
  // pantry items don't have cuts; surfacing "+ cut" as MISSING
  // for gjetost / butter / honey is wrong UX. Aligns with
  // CLAUDE.md's "CUTS_FOR is meats only — chicken/beef/pork/turkey"
  // canonical-per-animal rule.
  const HUBS_WITH_CUTS = new Set(["chicken_hub", "beef_hub", "pork_hub", "turkey_hub"]);
  const ing = findIngredient(draft.canonicalId);
  const hasCutAxis = ing?.parentId && HUBS_WITH_CUTS.has(ing.parentId);
  const applicableAxes = AXIS_DEFS.filter((a) => {
    if (a.key === "cut" && !hasCutAxis) return false;
    if (hiddenAxes.includes(a.key)) return false;
    return true;
  });
  const found = applicableAxes.filter((a) => isFound(draft, a));
  const missing = applicableAxes.filter((a) => !isFound(draft, a));
  const sizeFound = draft.packageAmount && draft.packageUnit;

  // Synthetic-canonical proposal — only render when no canonical is
  // bound yet (proposal is moot once user picked one) and the OFF
  // resolver staged a draft. User reviews / renames before any DB
  // write happens.
  const proposal = !draft.canonicalId ? draft.proposedCanonical : null;

  const canPlace = !!draft.canonicalId && sizeFound;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {showHeader && <Header upc={draft.barcodeUpc} />}

      {proposal && (
        <ProposalRouteCTA
          suggestedName={proposal.suggestedName}
          onOpen={onOpenPhotoCapture}
          onDismiss={onDismissProposal}
        />
      )}

      {found.length > 0 && (
        <Bucket
          kicker="FOUND"
          subtitle="Tap to override"
          accent="#7eb89b"
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {found.map((axis) => (
              <FoundChip
                key={axis.key}
                axis={axis}
                value={axisDisplayValue(draft, axis)}
                onClick={() => onOpenAxis?.(axis.key)}
              />
            ))}
            {sizeFound && (
              <FoundChip
                axis={{ key: "size", label: "Size", tone: CHIP_TONES.canonical }}
                value={`${draft.packageAmount} ${draft.packageUnit}`}
                onClick={onEditSize}
              />
            )}
          </div>
        </Bucket>
      )}

      {(missing.length > 0 || !sizeFound) && (
        <Bucket
          kicker="MISSING"
          subtitle="Tap to fill"
          accent="#d48a7a"
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {missing.map((axis) => (
              <MissingChip
                key={axis.key}
                axis={axis}
                onClick={() => onOpenAxis?.(axis.key)}
              />
            ))}
            {!sizeFound && (
              <MissingChip
                axis={{ key: "size", label: "Size", tone: CHIP_TONES.canonical }}
                onClick={onEditSize}
              />
            )}
          </div>
        </Bucket>
      )}

      {/* Action row */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginTop: 8,
      }}>
        <button
          onClick={onPlace}
          disabled={!canPlace || placing}
          style={primaryAction(canPlace && !placing)}
        >
          {placing ? "Placing…" : "Place in pantry"}
        </button>
        {onAddToList && (
          <button
            onClick={onAddToList}
            disabled={!canPlace || placing}
            style={{
              ...secondaryAction(),
              flex: undefined,
              width: "100%",
              fontWeight: 600,
              opacity: canPlace && !placing ? 1 : 0.5,
            }}
          >
            Add to shopping list
          </button>
        )}
        {(onPhoto || onManual) && (
          <div style={{ display: "flex", gap: 8 }}>
            {onPhoto && (
              <button onClick={onPhoto} style={secondaryAction()}>
                Take a photo
              </button>
            )}
            {onManual && (
              <button onClick={onManual} style={secondaryAction()}>
                Add manually
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Header({ upc }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{
        fontFamily: font.mono,
        fontSize: 10,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: color.inkMuted,
      }}>
        Scan review
      </div>
      <h2 style={{
        fontFamily: font.detail,
        fontStyle: "italic",
        fontWeight: 500,
        fontSize: 22,
        lineHeight: 1.15,
        color: color.ink,
        margin: 0,
      }}>
        What we know about this barcode.
      </h2>
      {upc && (
        <div style={{
          fontFamily: font.mono,
          fontSize: 10,
          color: color.inkFaint,
          letterSpacing: "0.06em",
        }}>
          UPC {upc}
        </div>
      )}
    </div>
  );
}

function Bucket({ kicker, subtitle, accent, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: accent,
          fontWeight: 600,
        }}>
          {kicker}
        </span>
        <span style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 12,
          color: color.inkMuted,
        }}>
          {subtitle}
        </span>
      </div>
      {children}
    </div>
  );
}

function FoundChip({ axis, value, onClick }) {
  const tone = axis.tone;
  return (
    <button
      onClick={(e) => { e.preventDefault(); onClick?.(); }}
      style={{
        ...SET_CHIP(tone),
        // Override font sizing slightly larger for review view —
        // the scan-review surface is the user's main read; chips
        // should be glanceable.
        fontSize: 10,
        padding: "4px 8px",
      }}
    >
      <span style={{
        fontFamily: font.mono,
        fontSize: 8,
        letterSpacing: "0.12em",
        marginRight: 6,
        opacity: 0.7,
      }}>
        {axis.label.toUpperCase()}
      </span>
      <span>{String(value).toUpperCase()}</span>
    </button>
  );
}

function MissingChip({ axis, onClick }) {
  return (
    <button
      onClick={(e) => { e.preventDefault(); onClick?.(); }}
      style={{
        ...UNSET_CHIP,
        fontSize: 10,
        padding: "4px 8px",
      }}
    >
      + {axis.label.toLowerCase()}
    </button>
  );
}

function primaryAction(enabled) {
  return {
    width: "100%",
    padding: "14px 18px",
    fontFamily: font.sans,
    fontSize: 15,
    fontWeight: 600,
    color: enabled ? color.cream : color.inkFaint,
    background: enabled ? color.ink : "transparent",
    border: `1px solid ${enabled ? color.ink : color.hairline}`,
    borderRadius: radius.field || 12,
    cursor: enabled ? "pointer" : "not-allowed",
    transition: "background 120ms ease, color 120ms ease",
  };
}

function secondaryAction() {
  return {
    flex: 1,
    padding: "12px 14px",
    fontFamily: font.sans,
    fontSize: 13,
    fontWeight: 500,
    color: color.ink,
    background: "transparent",
    border: `1px solid ${color.hairline}`,
    borderRadius: radius.field || 12,
    cursor: "pointer",
  };
}

// Proposal card — shown when OFF returned a productName + category
// hints but no bundled canonical matched. The user reviews the
// AI/OFF-suggested name (editable), then confirms (mints synthetic)
// or dismisses (leaves canonical unbound; user picks via search).
//
// This card is the SOLE place a synthetic canonical can be created
// from scan data. Auto-creation was a regression risk: marketing
// strings like "Premium Root Beer" or "Ski Queen Original" would
// silently mint synthetic registry rows that polluted the canonical
// namespace. Human review fixes that — the user sees the proposed
// name in plain English and edits before any DB write happens.
//
// Tone: celebratory, conversational, magical. The user is doing the
// platform a favor by minting a new ingredient; the copy should
// reflect that ("WE THINK WE FOUND A NEW ONE"), explain the dual-
// tier promotion ("you can use it today; admin reviews for global
// release"), and invite a name correction.
export function ProposalCard({ proposal, onConfirm, onDismiss }) {
  const [name, setName] = useState(proposal?.suggestedName || "");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const trimmed = name.trim();
  const canConfirm = trimmed.length >= 2 && !submitting && !confirmed;

  async function handleConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm?.(trimmed);
      setConfirmed(true);
    } finally {
      setSubmitting(false);
    }
  }

  // Post-confirm celebratory state. Stays visible briefly so the
  // user feels closure ("Cool, it's done") before the parent
  // unmounts the card. Parent typically clears the proposal from
  // the store immediately on confirm, but if it doesn't, the user
  // sees this confirmation flash.
  if (confirmed) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "14px 14px",
        borderRadius: radius.field || 12,
        background: `${CHIP_TONES.canonical.fg}18`,
        border: `1px solid ${CHIP_TONES.canonical.fg}66`,
      }}>
        <div style={{
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.20em",
          textTransform: "uppercase",
          color: CHIP_TONES.canonical.fg,
          fontWeight: 700,
        }}>
          Minted ✓
        </div>
        <div style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 15,
          color: color.ink,
          fontWeight: 500,
        }}>
          {trimmed} is yours to use today.
        </div>
        {/* Tier badge — freshly-minted synthetics start GREY. They
            promote to GREEN once admin approves the global row, and
            BLUE once verified nutrition (USDA / UPC tie-back) lands. */}
        <TierBadge tier="grey" showBlurb={false} />
        <div style={{
          fontFamily: font.sans,
          fontSize: 12,
          color: color.inkMuted,
          lineHeight: 1.4,
        }}>
          Sent up for review. If approved, everyone using the app
          gets to use it too — and the dot turns green.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: "14px 14px",
      borderRadius: radius.field || 12,
      background: `${CHIP_TONES.canonical.fg}10`,
      border: `1px solid ${CHIP_TONES.canonical.fg}40`,
    }}>
      <div style={{
        fontFamily: font.mono,
        fontSize: 10,
        letterSpacing: "0.20em",
        textTransform: "uppercase",
        color: CHIP_TONES.canonical.fg,
        fontWeight: 700,
      }}>
        ✨ We think we found a new one!
      </div>
      <div style={{
        fontFamily: font.detail,
        fontStyle: "italic",
        fontSize: 14,
        color: color.ink,
        lineHeight: 1.4,
      }}>
        Want to mint it? You can use this item today for cooks and
        we'll send it up for review. If approved, everyone using the
        app gets to use it too.
      </div>
      <div style={{
        fontFamily: font.sans,
        fontSize: 12,
        color: color.inkMuted,
        marginTop: 2,
      }}>
        Our name suggestion — does this look right? Edit if it's
        flavor or marketing copy ("Premium", "Wild Cherry").
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Canonical name…"
        style={{
          width: "100%",
          padding: "12px 14px",
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 17,
          fontWeight: 500,
          color: color.ink,
          background: color.cream,
          border: `1px solid ${CHIP_TONES.canonical.fg}55`,
          borderRadius: radius.field || 10,
          outline: "none",
        }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={(e) => { e.preventDefault(); handleConfirm(); }}
          disabled={!canConfirm}
          style={{
            flex: 2,
            padding: "12px 14px",
            fontFamily: font.sans,
            fontSize: 13,
            fontWeight: 600,
            color: canConfirm ? color.cream : color.inkFaint,
            background: canConfirm ? CHIP_TONES.canonical.fg : "transparent",
            border: `1px solid ${canConfirm ? CHIP_TONES.canonical.fg : color.hairline}`,
            borderRadius: radius.field || 10,
            cursor: canConfirm ? "pointer" : "not-allowed",
          }}
        >
          {submitting
            ? "Saving…"
            : `Save "${trimmed || "—"}" to canonicals`}
        </button>
        <button
          onClick={(e) => { e.preventDefault(); onDismiss?.(); }}
          style={{
            flex: 1,
            padding: "12px 14px",
            fontFamily: font.sans,
            fontSize: 13,
            fontWeight: 500,
            color: color.inkMuted,
            background: "transparent",
            border: `1px solid ${color.hairline}`,
            borderRadius: radius.field || 10,
            cursor: "pointer",
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

// Route-to-capture CTA. Replaces the inline ProposalCard inside
// ScanReview so the actual mint UX lives in one place
// (MemoryBookCapture → PhotoPipeline) instead of duplicating it on
// every surface that might need to mint a synthetic. The card here is
// intentionally a teaser — it tells the user what OFF suspects ("looks
// like Sake") and offers two actions: open the photo-bind flow (which
// owns the Haiku resolver + the canonical mint aesthetics) or skip
// and bind manually. Same pattern Cook Mode follows — one scanner, one
// entry point, reused everywhere a scan is needed.
function ProposalRouteCTA({ suggestedName, onOpen, onDismiss }) {
  const name = (suggestedName || "").trim();
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: "14px 14px",
      borderRadius: radius.field || 12,
      background: `${CHIP_TONES.canonical.fg}10`,
      border: `1px solid ${CHIP_TONES.canonical.fg}40`,
    }}>
      <div style={{
        fontFamily: font.mono,
        fontSize: 10,
        letterSpacing: "0.20em",
        textTransform: "uppercase",
        color: CHIP_TONES.canonical.fg,
        fontWeight: 700,
      }}>
        ✨ Bind a canonical
      </div>
      <div style={{
        fontFamily: font.detail,
        fontStyle: "italic",
        fontSize: 14,
        color: color.ink,
        lineHeight: 1.4,
      }}>
        {name
          ? <>Looks like <strong style={{ fontWeight: 700 }}>{name}</strong>, but we couldn&apos;t bind it from the barcode alone.</>
          : <>We couldn&apos;t bind this to a known canonical from the barcode alone.</>}
        {" "}
        Take a quick photo and we&apos;ll let our resolver handle it.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={(e) => { e.preventDefault(); onOpen?.(); }}
          style={{
            flex: 2,
            padding: "12px 14px",
            fontFamily: font.sans,
            fontSize: 13,
            fontWeight: 600,
            color: color.cream,
            background: CHIP_TONES.canonical.fg,
            border: `1px solid ${CHIP_TONES.canonical.fg}`,
            borderRadius: radius.field || 10,
            cursor: "pointer",
          }}
        >
          📷 Bind with a photo
        </button>
        <button
          onClick={(e) => { e.preventDefault(); onDismiss?.(); }}
          style={{
            flex: 1,
            padding: "12px 14px",
            fontFamily: font.sans,
            fontSize: 13,
            fontWeight: 500,
            color: color.inkMuted,
            background: "transparent",
            border: `1px solid ${color.hairline}`,
            borderRadius: radius.field || 10,
            cursor: "pointer",
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
